import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.js'
import { useReveal } from '../hooks/useReveal.js'
import { createCheckout, createTopupCheckout } from '../lib/api.js'
import { cn } from '../lib/cn.js'

// ── Tier data moved inside component for i18n ────────────────────────────────

const BUNDLES = [
  { id: 'bundle-basic', ext: 'Plus', studio: 'Basic', monthly: 24, savings: 4 },
  { id: 'bundle-pro',   ext: 'Pro',  studio: 'Pro',   monthly: 59, savings: 9 },
]

const TOPUPS = [
  { minutes: 20,  price: 15  },
  { minutes: 60,  price: 39  },
  { minutes: 200, price: 110 },
  { minutes: 600, price: 270 },
]

// ── Component ────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'extension' | 'studio'>('extension')
  const [loading, setLoading] = useState<string | null>(null)

  useReveal()

  const extensionTiers = [
    { id: 'ext-free', label: t('landing.tiers.tierFree'), monthly: 0, minutes: t('landing.tiers.fiveMinTrial'), features: ['pricing.ext.feat.fiveLangs', 'pricing.ext.feat.watermarked'] },
    { id: 'ext-plus', label: t('landing.tiers.tierPlus'), monthly: 9, minutes: t('landing.tiers.minPerMo', { count: 300 }), features: ['pricing.ext.feat.fiveLangs', 'pricing.ext.feat.noWatermark'] },
    { id: 'ext-pro', label: t('landing.tiers.tierPro'), monthly: 19, minutes: t('landing.tiers.minPerMo', { count: 1000 }), features: ['pricing.ext.feat.fiveLangs', 'pricing.ext.feat.noWatermark', 'pricing.ext.feat.priority'] },
  ]
  const studioTiers = [
    { id: 'studio-free', label: t('landing.tiers.tierFree'), monthly: 0, minutes: t('landing.tiers.fiveMinTrial'), features: ['pricing.studio.feat.allLangs', 'pricing.studio.feat.watermarked', 'pricing.studio.feat.oneVoice'] },
    { id: 'studio-basic', label: t('landing.tiers.tierBasic'), monthly: 19, minutes: t('landing.tiers.minPerMo', { count: 30 }), popular: true, features: ['pricing.studio.feat.allLangs', 'pricing.studio.feat.noWatermark', 'pricing.studio.feat.voicePair'] },
    { id: 'studio-pro', label: t('landing.tiers.tierPro'), monthly: 49, minutes: t('landing.tiers.minPerMo', { count: 120 }), features: ['pricing.studio.feat.allLangs', 'pricing.studio.feat.multiSpeaker', 'pricing.studio.feat.emotion'] },
  ]

  const handleSubscribe = async (tierId: string) => {
    if (tierId.endsWith('-free')) {
      navigate(user ? '/dashboard' : '/auth')
      return
    }
    if (!user) { navigate('/auth'); return }
    setLoading(tierId)
    try {
      const { url } = await createCheckout(tierId, `${window.location.origin}/usage?success=1`, `${window.location.origin}/pricing`)
      window.location.href = url
    } catch { setLoading(null) }
  }

  const handleTopup = async (minutes: number) => {
    if (!user) { navigate('/auth'); return }
    setLoading(`topup-${minutes}`)
    try {
      const { url } = await createTopupCheckout(minutes, `${window.location.origin}/usage?success=1`, `${window.location.origin}/pricing`)
      window.location.href = url
    } catch { setLoading(null) }
  }

  const tiers = tab === 'extension' ? extensionTiers : studioTiers

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '80px 40px 60px' }}>
      {/* Header */}
      <div className="reveal" style={{ textAlign: 'center', marginBottom: 48 }}>
        <span className="label" style={{ marginBottom: 10 }}>{t('pricing.label', 'Pricing')}</span>
        <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
          {t('pricing.title1', 'Simple, ')}<em className="serif">{t('pricing.title2', 'transparent')}</em>{t('pricing.title3', ' pricing')}
        </h2>
        <p style={{ fontSize: 16, color: 'var(--color-text-muted)', marginTop: 14, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
          {t('pricing.subtitle')}
        </p>
      </div>

      {/* Extension / Studio toggle */}
      <div className="reveal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
        <div className="pricing-toggle">
          <button className={cn('pricing-toggle-btn', tab === 'extension' && 'active')} onClick={() => setTab('extension')}>
            {t('pricing.tab.extension', 'Extension')} <span className="coming-soon-badge" style={{ marginLeft: 6, fontSize: 9, padding: '2px 6px' }}>{t('comingSoon')}</span>
          </button>
          <button className={cn('pricing-toggle-btn', tab === 'studio' && 'active')} onClick={() => setTab('studio')}>
            {t('pricing.tab.studio', 'Studio Dub')}
          </button>
        </div>
      </div>

      {/* Product description */}
      <div className="reveal" style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', maxWidth: 480, margin: '0 auto' }}>
          {tab === 'extension'
            ? t('pricing.ext.desc', 'Real-time dubbing as you watch YouTube. Works in Chrome with 5 languages.')
            : t('pricing.studio.desc', 'Studio-quality async dubbing. Download MP3s in any of 33 languages.')
          }
        </p>
      </div>

      {/* Tier cards */}
      <div className="reveal pricing-grid">
        {tiers.map((tier) => {
          const price = tier.monthly
          const isLoading = loading === tier.id
          const popular = 'popular' in tier && (tier as { popular?: boolean }).popular
          const isExt = tab === 'extension'

          return (
            <div key={tier.id} className={cn('pricing-card', popular && 'featured')} style={isExt ? { opacity: 0.55, pointerEvents: 'none' } : undefined}>
              {isExt && <span className="coming-soon-badge" style={{ position: 'absolute', top: 12, right: 12, fontSize: 10 }}>{t('comingSoon')}</span>}
              {popular && <span className="pricing-badge">{t('pricing.mostPopular')}</span>}

              <p style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{tier.label}</p>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 4 }}>
                {price === 0 ? (
                  <span style={{ fontSize: 42, fontWeight: 800, lineHeight: 1 }}>{t('landing.tiers.free')}</span>
                ) : (
                  <>
                    <sup style={{ fontSize: 18, fontWeight: 700, opacity: 0.5, verticalAlign: 'super' }}>$</sup>
                    <span style={{ fontSize: 42, fontWeight: 800, lineHeight: 1 }}>{price % 1 === 0 ? price : price.toFixed(2)}</span>
                    <sub style={{ fontSize: 14, opacity: 0.5, marginLeft: 2 }}>{t('pricing.perMonth')}</sub>
                  </>
                )}
              </div>

              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 4 }}>{tier.minutes}</p>

              <button
                className={cn(popular ? 'pricing-cta-fill' : 'pricing-cta-line', (isLoading || isExt) && 'opacity-60 cursor-not-allowed')}
                onClick={() => !isLoading && !isExt && void handleSubscribe(tier.id)}
                disabled={isLoading || isExt}
                style={{ width: '100%', marginTop: 16, marginBottom: 20 }}
              >
                {isExt ? t('comingSoon') : isLoading ? '...' : tier.monthly === 0 ? t('pricing.getStarted') : t('pricing.subscribe', { tier: tier.label })}
              </button>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tier.features.map((fk) => (
                  <li key={fk} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--color-text-muted)' }}>
                    <CheckCircle />
                    {t(fk as never)}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      {/* Bundles */}
      <div className="reveal" style={{ marginTop: 48 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <span className="label" style={{ marginBottom: 10 }}>{t('pricing.bundle.label', 'Bundle & Save')}</span>
          <h3 style={{ fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {t('pricing.bundle.title', 'Get both products for less')}
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, maxWidth: 600, margin: '0 auto' }}>
          {BUNDLES.map((b) => {
            return (
              <div key={b.id} className="pricing-card" style={{ textAlign: 'center', opacity: 0.55, pointerEvents: 'none', position: 'relative' }}>
                <span className="coming-soon-badge" style={{ position: 'absolute', top: 10, right: 10, fontSize: 9 }}>{t('comingSoon')}</span>
                <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                  {t('landing.tiers.bundleLabel', { ext: t('landing.tiers.tier' + b.ext), studio: t('landing.tiers.tier' + b.studio) })}
                </p>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, marginBottom: 4 }}>
                  <sup style={{ fontSize: 14, fontWeight: 700, opacity: 0.5, verticalAlign: 'super' }}>$</sup>
                  <span style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{b.monthly}</span>
                  <sub style={{ fontSize: 13, opacity: 0.5, marginLeft: 2 }}>{t('pricing.perMonth')}</sub>
                </div>
                <span style={{
                  display: 'inline-block', fontSize: 11, fontWeight: 700,
                  background: 'linear-gradient(135deg,#FF006E,#FFBE0B)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  marginBottom: 14,
                }}>
                  {t('pricing.bundle.save', { amount: b.savings })}
                </span>
                <button
                  className="pricing-cta-fill opacity-60 cursor-not-allowed"
                  disabled
                  style={{ width: '100%' }}
                >
                  {t('comingSoon')}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Top-up packs (studio minutes only) */}
      <div className="reveal" style={{ marginTop: 72, textAlign: 'center' }}>
        <span className="label" style={{ marginBottom: 10 }}>{t('pricing.topups.label', 'Top-up packs')}</span>
        <h3 style={{ fontSize: 'clamp(22px, 2.5vw, 32px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
          {t('pricing.topups.title')}
        </h3>
        <p style={{ fontSize: 15, color: 'var(--color-text-muted)', marginTop: 8 }}>
          {t('pricing.topups.subtitle')}
        </p>
      </div>

      <div className="reveal topup-grid" style={{ marginTop: 32 }}>
        {TOPUPS.map(({ minutes, price }) => {
          const isLoading = loading === `topup-${minutes}`
          const perMin = (price / minutes).toFixed(2)
          return (
            <div key={minutes} className="pricing-card" style={{ textAlign: 'center' }}>
              <p style={{
                fontSize: 28, fontWeight: 800, marginBottom: 2,
                background: 'linear-gradient(135deg, #FF006E, #FFBE0B)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>
                {minutes} {t('pricing.topups.min')}
              </p>
              <p style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>${price}</p>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16 }}>{t('pricing.topups.perMin', { price: perMin })}</p>
              <button
                onClick={() => !isLoading && void handleTopup(minutes)}
                disabled={isLoading}
                className={cn('pricing-cta-line', isLoading && 'opacity-60 cursor-not-allowed')}
                style={{ width: '100%' }}
              >
                {isLoading ? '...' : t('pricing.topups.buyBtn')}
              </button>
            </div>
          )
        })}
      </div>

      {/* Bottom note */}
      <div className="reveal" style={{ textAlign: 'center', marginTop: 40, fontSize: 13, color: 'var(--color-text-muted)' }}>
        <p>{t('pricing.topups.neverExpire', 'Top-up minutes never expire. Studio minutes only.')}</p>
      </div>

      <style>{`
        .pricing-toggle {
          display: inline-flex;
          border: 1px solid var(--color-glass-border);
          border-radius: 10px;
          overflow: hidden;
          background: var(--color-surface);
        }
        .pricing-toggle-btn {
          padding: 8px 20px;
          font-size: 13px;
          font-weight: 600;
          border: none;
          background: transparent;
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
        }
        .pricing-toggle-btn.active {
          background: linear-gradient(135deg, #FF006E, #FFBE0B);
          color: #fff;
        }
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          align-items: start;
        }
        .topup-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .pricing-card {
          background: var(--color-surface);
          border: 1px solid var(--color-glass-border);
          border-radius: var(--radius-lg);
          padding: 28px 24px;
          position: relative;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .pricing-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }
        .pricing-card.featured {
          border-color: var(--color-accent);
        }
        .pricing-badge {
          position: absolute;
          top: -1px;
          right: 18px;
          background: linear-gradient(135deg, #FF006E, #FFBE0B);
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 5px 12px;
          border-radius: 0 0 7px 7px;
        }
        .pricing-cta-fill {
          display: block;
          padding: 11px 0;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #FF006E, #FFBE0B);
          color: #fff;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
          text-align: center;
        }
        .pricing-cta-fill:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .pricing-cta-line {
          display: block;
          padding: 11px 0;
          border-radius: 10px;
          border: 1.5px solid var(--color-glass-border);
          background: transparent;
          color: var(--color-text);
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
          text-align: center;
        }
        .pricing-cta-line:hover:not(:disabled) {
          background: var(--color-surface-raised);
          transform: translateY(-1px);
        }
        @media (max-width: 860px) {
          .pricing-grid {
            grid-template-columns: 1fr;
            max-width: 400px;
            margin-left: auto;
            margin-right: auto;
          }
        }
        @media (max-width: 560px) {
          .topup-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  )
}

function CheckCircle() {
  return (
    <span style={{
      width: 14, height: 14, borderRadius: '50%',
      background: 'var(--color-accent)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="7" height="7" viewBox="0 0 10 10" fill="none">
        <path d="M2 5.5L4 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}
